import React from 'react';
import { Typography } from 'antd';
import styled from '@emotion/styled';

const { Text } = Typography;

// mock chat colours, deliberately not design tokens: this previews WhatsApp's own ui, not ours
const BUBBLE_BLUE = '#007aff';
const ACTION_GREY = '#f0f0f0';

export interface MessageBubbleProps {
  /** The name of the user or recipient */
  firstName?: string;
  /** The message content to display */
  message: string;
  /** Rendered above the text, as a template image header appears on a fan's phone */
  imageUrl?: string;
  /** Describes the header image; it is composed content, not decoration */
  imageAlt?: string;
  /** Rendered under the text as a tappable call to action */
  buttonText?: string;
  /** Shown under the button so the composer can see where a tap lands */
  buttonUrl?: string;
  /** Custom class name */
  className?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 375px;
`;

const BubbleContainer = styled.div`
  position: relative;
  padding: 12px 16px;
  border-radius: 15px;
  background-color: ${BUBBLE_BLUE};
  color: white;
  max-width: 70%;
  align-self: flex-start;
  margin-bottom: 4px;

  &::after {
    content: '';
    position: absolute;
    left: -4px;
    bottom: 0;
    width: 16px;
    height: 12px;
    background: ${BUBBLE_BLUE};
    border-radius: 0 0 0 12px;
    transform: rotate(-45deg);
    z-index: 0;
  }
`;

const HeaderImage = styled.img`
  max-width: 70%;
  border-radius: 12px;
  margin-bottom: 6px;
  align-self: flex-start;
  object-fit: cover;
`;

const ActionButton = styled.div`
  max-width: 70%;
  align-self: flex-start;
  padding: 8px 12px;
  border-radius: 12px;
  background-color: ${ACTION_GREY};
  color: ${BUBBLE_BLUE};
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  word-break: break-word;
`;

const ActionUrl = styled(Text)`
  max-width: 70%;
  align-self: flex-start;
  margin-top: 2px;
  font-size: 12px;
  word-break: break-all;
`;

const MessageText = styled(Text)`
  color: white;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.3;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageBubble: React.FC<MessageBubbleProps> = ({
  firstName,
  message,
  imageUrl,
  imageAlt,
  buttonText,
  buttonUrl,
  className,
}) => {
  // Format the message with the user name if provided
  const displayMessage = firstName
    ? message.replace('{{First Name}}', firstName)
    : message;

  return (
    <Container className={className}>
      {imageUrl && <HeaderImage src={imageUrl} alt={imageAlt ?? ''} />}
      <BubbleContainer>
        <MessageText>{displayMessage}</MessageText>
      </BubbleContainer>
      {buttonText && <ActionButton>{buttonText}</ActionButton>}
      {buttonText && buttonUrl && (
        <ActionUrl type="secondary">{buttonUrl}</ActionUrl>
      )}
    </Container>
  );
};

export default MessageBubble;
